// SPDX-License-Identifier: AGPL-3.0-or-later

import {http} from '@app/features/platform/transport/RestTransport';
import type {BotCommand} from '@app/features/devtools/hooks/useCommands';
import {makeAutoObservable, runInAction} from 'mobx';

export interface StoredBotCommand {
	id: string;
	application_id: string;
	guild_id?: string | null;
	name: string;
	description: string;
	type: number;
	options?: any[];
	bot?: {
		id: string;
		username: string;
		avatar?: string | null;
	};
}

export interface ContextMenuCommand {
	commandId: string;
	applicationId: string;
	name: string;
	commandType: 2 | 3;
	botName: string;
}

export class ApplicationCommandsStore {
	private readonly guildCommands = new Map<string, Array<BotCommand>>();
	private readonly guildContextCommands = new Map<string, Array<ContextMenuCommand>>();
	private readonly loadingGuilds = new Set<string>();
	private readonly lastFetched = new Map<string, number>();
	version: number = 0;

	constructor() {
		makeAutoObservable(this);
	}

	public getCommandsForGuild(guildId?: string | null): Array<BotCommand> {
		if (!guildId) return [];
		void this.version;
		return this.guildCommands.get(guildId) ?? [];
	}

	public getContextCommands(guildId: string | null | undefined, commandType: 2 | 3): Array<ContextMenuCommand> {
		if (!guildId) return [];
		void this.version;
		return (this.guildContextCommands.get(guildId) ?? []).filter((command) => command.commandType === commandType);
	}

	public async fetchCommandsForGuild(guildId: string, force = false): Promise<void> {
		const now = Date.now();
		const last = this.lastFetched.get(guildId) ?? 0;
		const existing = this.guildCommands.get(guildId);

		// Don't re-fetch if already in-flight
		if (this.loadingGuilds.has(guildId)) {
			return;
		}

		// If already has commands and fetched recently, skip unless forced or empty
		if (!force && existing && existing.length > 0 && now - last < 30000) {
			return;
		}

		this.loadingGuilds.add(guildId);
		try {
			const res = await http.get<Array<StoredBotCommand>>(`/guilds/${guildId}/application-commands`);
			if (res && Array.isArray(res.body)) {
				const contextCommands: Array<ContextMenuCommand> = res.body
					.filter((raw: StoredBotCommand) => raw.type === 2 || raw.type === 3)
					.map((raw: StoredBotCommand) => ({
						commandId: raw.id,
						applicationId: raw.application_id,
						name: raw.name,
						commandType: raw.type as 2 | 3,
						botName: raw.bot?.username ?? 'Bot',
					}));
				// Context menu commands never show up in the slash command picker.
				const botCommands: Array<BotCommand> = res.body
					.filter((raw: StoredBotCommand) => (raw.type ?? 1) === 1)
					.map((raw: StoredBotCommand) => ({
					type: 'bot',
					name: raw.name.startsWith('/') ? raw.name : `/${raw.name}`,
					description: raw.description,
					applicationId: raw.application_id,
					commandId: raw.id,
					botId: raw.bot?.id ?? raw.application_id,
					botName: raw.bot?.username ?? 'Bot',
					botAvatar: raw.bot?.avatar ?? null,
					requiresGuild: Boolean(raw.guild_id),
					options: (raw.options ?? []).map((opt: any) => ({
						name: opt.name,
						description: opt.description,
						type: opt.type === 4 ? ('integer' as const) : ('string' as const),
						required: Boolean(opt.required),
						choices: opt.choices ?? [],
					})),
				}));

				runInAction(() => {
					this.guildCommands.set(guildId, botCommands);
					this.guildContextCommands.set(guildId, contextCommands);
					this.lastFetched.set(guildId, now);
					this.version++;
				});
			}
		} catch {
			// Ignore fetch errors, allow retry
		} finally {
			runInAction(() => {
				this.loadingGuilds.delete(guildId);
			});
		}
	}
}

export default new ApplicationCommandsStore();
